import { CheckOutlined, SearchOutlined } from '@ant-design/icons';
import { AutoComplete, Button, Input } from 'antd';
import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

const SUGGESTION_MAX = 200;
const VOTE_AC_PREFIX = '__vote__';

function filterCatalogByCountry(items, countryFilter, catalogKind) {
  if (!items?.length) return [];
  if (!countryFilter || countryFilter === 'all') return items;
  if (catalogKind === 'team') {
    return items.filter((t) => {
      const code =
        t.countryCode || (/^[a-z]{2}$/i.test(String(t.id)) ? String(t.id).toLowerCase() : '');
      return code === countryFilter;
    });
  }
  return items.filter((p) => String(p.countryCode || '').toLowerCase() === countryFilter);
}

function filterCatalogByQuery(items, query) {
  const q = query.trim().toLowerCase();
  if (!q) return items;
  return items.filter((p) => p.name.toLowerCase().includes(q));
}

const RANK_CONFIG = {
  1: {
    height: 'h-[190px] lg:h-[220px] lg:max-[1100px]:h-[174px]',
    frameSrc: '/assets/images/match/frame-center.png',
    frameAspect: '130 / 191',
    avatarSize: 'w-[44%] aspect-square lg:w-[46%]',
    avatarTop: 'top-[5%]',
    textBottom: 'bottom-[29%]'
  },
  2: {
    height: 'h-[150px] lg:h-[178px] lg:max-[1100px]:h-[138px]',
    frameSrc: '/assets/images/match/frame-leftright.png',
    frameAspect: '103 / 150',
    avatarSize: 'w-[43%] aspect-square lg:w-[45%]',
    avatarTop: 'top-[5%]',
    textBottom: 'bottom-[22%]'
  },
  3: {
    height: 'h-[150px] lg:h-[178px] lg:max-[1100px]:h-[138px]',
    frameSrc: '/assets/images/match/frame-leftright.png',
    frameAspect: '103 / 150',
    avatarSize: 'w-[43%] aspect-square lg:w-[45%]',
    avatarTop: 'top-[5%]',
    textBottom: 'bottom-[22%]'
  }
};

const LeaderboardPanel = ({ top3, rows, resolvePhoto, onPickRow }) => {
  const podium = [{ rank: 2, ...top3[1] }, { rank: 1, ...top3[0] }, { rank: 3, ...top3[2] }].filter(
    (x) => x?.id
  );
  const pickable = typeof onPickRow === 'function';
  return (
    <div
      className="lg:col-span-2 rounded-2xl p-4 shadow-sm"
      style={{
        border: '1.5px solid transparent',
        background:
          'linear-gradient(180deg, #FDF8F0 0%, rgba(248, 148, 61, 0.05) 100%) padding-box, linear-gradient(180deg, #FAC6A6 0%, #F58D4D 100%) border-box'
      }}
    >
      <div className="grid grid-cols-3 gap-3 xl:gap-10 min-[1024px]:max-[1100px]:gap-8 items-start">
        {podium.map((item) => {
          const cfg = RANK_CONFIG[item.rank];
          const photo = resolvePhoto(item);
          const inner = (
            <>
              <div className={twMerge('relative w-full flex items-center justify-center', cfg.height)}>
                <div className="relative mx-auto h-full" style={{ aspectRatio: cfg.frameAspect }}>
                  <img
                    src={cfg.frameSrc}
                    alt="podium-frame"
                    className="w-full h-full object-fill select-none pointer-events-none"
                    draggable={false}
                  />

                  <div
                    className={twMerge(
                      'absolute left-1/2 -translate-x-1/2 rounded-full border border-[#e5e7eb] shadow-sm overflow-hidden bg-white',
                      cfg.avatarSize,
                      cfg.avatarTop
                    )}
                  >
                    {photo ? (
                      <img
                        src={photo}
                        alt=""
                        className="block h-full w-full origin-center scale-[1.18] object-cover object-center"
                      />
                    ) : (
                      <div className="w-full h-full bg-[#f3f4f6]" />
                    )}
                  </div>

                  <div
                    className={twMerge(
                      'absolute left-1/2 -translate-x-1/2 w-full text-center text-white px-1',
                      cfg.textBottom
                    )}
                  >
                    <div className="font-black text-[10px] lg:text-[11px] leading-none truncate drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">
                      {item.name}
                    </div>
                    <div className="mt-1 text-[10px] font-extrabold leading-none opacity-95 drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">
                      #{item.rank}
                    </div>
                    <div className="mt-1.5 text-[12px] font-black leading-none drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">
                      {Intl.NumberFormat('vi-VN').format(item.votes || 0)}
                    </div>
                  </div>
                </div>
              </div>
            </>
          );
          return (
            <div key={`${item.rank}-${item.id}`} className="flex flex-col items-center w-full min-w-0">
              {pickable ? (
                <button
                  type="button"
                  title="Chọn để điền vào ô dự đoán"
                  onClick={() => onPickRow(item)}
                  className="m-0 w-full max-w-[200px] cursor-pointer rounded-2xl border-0 bg-transparent p-0 text-left transition-opacity hover:opacity-95 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ef8a45] focus-visible:ring-offset-2"
                >
                  {inner}
                </button>
              ) : (
                inner
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4 rounded-xl border border-[#efc7ab] p-2 bg-white/80">
        {rows.slice(3, 10).map((row, idx) => {
          const order = idx + 4;
          const rowPhoto = resolvePhoto(row);
          const isOdd = order % 2 === 1;
          return (
            <div
              key={`${row.id}-${order}`}
              role={pickable ? 'button' : undefined}
              tabIndex={pickable ? 0 : undefined}
              onClick={pickable ? () => onPickRow(row) : undefined}
              onKeyDown={
                pickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onPickRow(row);
                      }
                    }
                  : undefined
              }
              title={pickable ? 'Chọn để điền vào ô dự đoán' : undefined}
              className={twMerge(
                'flex items-center justify-between px-3 py-2 text-sm rounded-[10px] border mb-2 last:mb-0',
                isOdd ? 'bg-[#fff3ea] border-[#f0c8aa]' : 'bg-white border-[#e8dccf]',
                pickable ? 'cursor-pointer transition-colors hover:bg-[#ffe8d6]/80 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ef8a45]' : ''
              )}
            >
              <div className="flex items-center gap-2 min-w-0">
                <div className="w-6 text-[#6b7280] ">{order}</div>
                <div className="relative h-7 w-7 shrink-0">
                  <div className="absolute inset-0 overflow-hidden rounded-full bg-white">
                    {rowPhoto ? (
                      <img
                        src={rowPhoto}
                        alt=""
                        className="block h-full w-full origin-center scale-[1.14] object-cover object-center"
                      />
                    ) : null}
                  </div>
                  <div
                    className="pointer-events-none absolute inset-0 z-[1] rounded-full border border-[#e5e7eb]"
                    aria-hidden
                  />
                </div>
                <div className=" text-[#374151] truncate">{row.name}</div>
              </div>
              <div className="text-[#374151]">{Intl.NumberFormat('vi-VN').format(row.votes || 0)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const VotePanel = ({
  title,
  query,
  onQueryChange,
  onSubmit,
  loading,
  items,
  selectedId,
  onSelect,
  emptyText,
  showTeamName,
  getItemPhoto,
  inputPlaceholder = 'Tìm cầu thủ yêu thích của bạn...',
  catalogItems,
  catalogKind = 'team',
  suggestionGroupLabel = 'Danh sách',
  countryFilter = 'all'
}) => {
  const catalogSource = catalogItems?.length ? catalogItems : items;

  const catalogForSuggest = useMemo(
    () => filterCatalogByCountry(catalogSource, countryFilter, catalogKind),
    [catalogSource, countryFilter, catalogKind]
  );

  const suggestionFiltered = useMemo(() => {
    const byQuery = filterCatalogByQuery(catalogForSuggest, query);
    return byQuery.slice(0, SUGGESTION_MAX);
  }, [catalogForSuggest, query]);

  const autoCompleteOptions = useMemo(() => {
    if (!suggestionFiltered.length) return [];
    return [
      {
        label: suggestionGroupLabel,
        options: suggestionFiltered.map((t) => ({
          value: `${VOTE_AC_PREFIX}${t.id}`,
          label:
            catalogKind === 'player' && showTeamName && t.teamName ? (
              <div className="flex flex-col gap-0">
                <span className="text-[13px] font-semibold text-[#111827]">{t.name}</span>
                <span className="text-[10px] text-[#9ca3af]">{t.teamName}</span>
              </div>
            ) : (
              <span className="text-[13px] font-semibold text-[#111827]">{t.name}</span>
            )
        }))
      }
    ];
  }, [suggestionFiltered, suggestionGroupLabel, catalogKind, showTeamName]);

  const handleSearchInputChange = (v) => {
    const s = v ?? '';
    if (typeof s === 'string' && s.startsWith(VOTE_AC_PREFIX)) {
      const id = s.slice(VOTE_AC_PREFIX.length);
      const hit = catalogForSuggest.find((x) => String(x.id) === String(id));
      if (hit) {
        onQueryChange(hit.name);
        onSelect(hit);
      }
      return;
    }
    onQueryChange(s);
  };

  return (
    <div
      className="lg:col-span-3 rounded-2xl p-4 shadow-sm lg:border-[1.5px] "
      style={{
        background:
          'linear-gradient(180deg, #FDF8F0 0%, rgba(248, 148, 61, 0.05) 100%) padding-box, linear-gradient(180deg, #FAC6A6 0%, #F58D4D 100%) border-box'
      }}
    >
      <div className="hidden lg:flex items-center justify-center w-full">
        <div className="relative w-full max-w-[760px] mx-auto px-2">
          <img
            src="/assets/images/match/frametitle.png"
            alt="Frame title"
            className="w-full h-auto object-contain select-none pointer-events-none"
            draggable={false}
          />
          <div className="absolute inset-0 flex items-center justify-center font-black uppercase text-white text-[20px] xl:text-[22px] px-3 text-center leading-tight">
            {title}
          </div>
        </div>
      </div>

      <div className="mt-3 flex items-center gap-3 max-w-[550px] mx-auto">
        <AutoComplete
          value={query}
          options={autoCompleteOptions}
          onChange={handleSearchInputChange}
          filterOption={false}
          allowClear
          className="flex-1 min-w-0"
          popupClassName="min-w-[min(360px,92vw)] max-h-[min(320px,50vh)] [&_.ant-select-item]:!py-2"
          popupMatchSelectWidth={false}
          notFoundContent={
            catalogForSuggest.length ? 'Không có kết quả' : 'Chưa có dữ liệu'
          }
        >
          <Input
            placeholder={inputPlaceholder}
            suffix={<SearchOutlined className="text-[#94a3b8] text-[14px]" />}
            className="h-[38px] lg:h-[44px] rounded-full text-[14px] lg:text-[16px] text-[#6b7280] placeholder:text-[#6b7280]"
            style={{ background: '#f8f8f8' }}
          />
        </AutoComplete>
        <Button
          type="primary"
          className="h-[38px] lg:h-[44px] min-w-[96px] lg:min-w-[136px] rounded-full px-4 lg:px-6 font-extrabold text-[13px] lg:text-[16px] leading-none border border-[#ffd5ad] shadow-[inset_0_1px_0_rgba(255,255,255,0.35)]"
          style={{
            background: 'linear-gradient(180deg, #f08b2f 0%, #df6b1d 100%)',
            color: '#fff'
          }}
          loading={loading}
          onClick={onSubmit}
        >
          Dự đoán
        </Button>
      </div>

      <div className="mt-4 max-h-[min(52vh,520px)] overflow-y-auto overflow-x-hidden pr-0.5 pb-1 scrollbar-thin-gray">
        <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
          {items.map((item) => {
            const isActive = String(selectedId) === String(item.id);
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item)}
                className={twMerge(
                  'relative rounded-xl border bg-white px-2 py-2 flex flex-col items-center gap-2 transition-all hover:shadow-md',
                  isActive ? 'border-[#ef8a45] ring-2 ring-[#ef8a45]/25' : 'border-[#d8dde4]'
                )}
              >
                {isActive ? (
                  <div className="absolute top-0 right-0 w-6 h-6 rounded-bl-lg rounded-tr-lg bg-[#ef8a45] flex items-center justify-center">
                    <CheckOutlined className="text-white text-[13px] font-black" />
                  </div>
                ) : null}
                <div className="relative h-12 w-12 shrink-0">
                  <div className="absolute inset-0 overflow-hidden rounded-full bg-white">
                    <img
                      src={getItemPhoto(item)}
                      alt=""
                      className="block h-full w-full origin-center scale-[1.22] object-cover object-center"
                      draggable={false}
                    />
                  </div>
                  <div
                    className="pointer-events-none absolute inset-0 z-[1] rounded-full border border-[#e5e7eb]"
                    aria-hidden
                  />
                </div>
                <div className="w-full min-w-0">
                  <div className="text-[12px] lg:text-[14px] font-black text-[#111827] text-center leading-tight line-clamp-2">
                    {item.name}
                  </div>
                  {showTeamName && item.teamName ? (
                    <div className="mt-0.5 text-[10px] lg:text-[11px] font-medium text-[#9ca3af] text-center leading-tight line-clamp-1">
                      {item.teamName}
                    </div>
                  ) : null}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="mt-6 text-center text-sm text-[#6b7280]">{emptyText}</div>
      ) : null}
    </div>
  );
};

const norm = (s) => String(s ?? '').trim().toLowerCase();

/** Khớp dòng BXH với catalog đội: id có thể là code (vn, br) khác kiểu so khớp strict từ parent */
const resolveTeamFromLeaderboardRow = (row, championTeams) => {
  if (!row?.id) return null;
  const rid = String(row.id).trim();
  const byId = championTeams.find((t) => norm(t.id) === norm(rid));
  if (byId) return byId;
  const rn = norm(row.name);
  if (!rn) return null;
  return championTeams.find((t) => norm(t.name) === rn) || null;
};

export const ChampionPredictionTab = ({
  championLeaderboard,
  championTeams,
  filteredChampionTeams,
  championQuery,
  setChampionQuery,
  isChampionVoting,
  onSubmitChampion,
  selectedChampionId,
  setSelectedChampionId,
  votePanelTitle = 'Dự đoán đội vô địch',
  searchPlaceholder = 'Tìm đội bóng yêu thích của bạn...',
  /** Khi BXH không khớp catalog: vẫn điền tên vào ô tìm (bật auto-fill) */
  fillChampionQueryFromPick
}) => {
  const top3 = championLeaderboard.slice(0, 3).map((row) => {
    const team = resolveTeamFromLeaderboardRow(row, championTeams);
    return { ...row, flag: row.flag ?? team?.flag };
  });

  const handlePickLeaderboardRow = (row) => {
    if (!row?.id) return;
    const team = resolveTeamFromLeaderboardRow(row, championTeams);
    const idToUse = team ? team.id : row.id;
    setSelectedChampionId(idToUse);
    if (!team?.name && row.name && typeof fillChampionQueryFromPick === 'function') {
      fillChampionQueryFromPick(row.name);
    }
  };

  return (
    <div className="w-full mt-3 grid grid-cols-1 lg:grid-cols-5 gap-4">
      <LeaderboardPanel
        top3={top3}
        rows={championLeaderboard}
        resolvePhoto={(x) => x.flag}
        onPickRow={handlePickLeaderboardRow}
      />
      <VotePanel
        title={votePanelTitle}
        query={championQuery}
        onQueryChange={setChampionQuery}
        onSubmit={onSubmitChampion}
        loading={isChampionVoting}
        items={filteredChampionTeams}
        selectedId={selectedChampionId}
        onSelect={(team) => setSelectedChampionId(team.id)}
        emptyText="Không tìm thấy đội phù hợp."
        showTeamName={false}
        getItemPhoto={(team) => team.flag}
        inputPlaceholder={searchPlaceholder}
        catalogItems={championTeams}
        catalogKind="team"
        suggestionGroupLabel="Danh sách đội tuyển"
      />
    </div>
  );
};

const resolvePlayerFromLeaderboardRow = (row, players) => {
  if (!row?.id) return null;
  const rid = String(row.id).trim();
  const byId = players.find((p) => norm(p.id) === norm(rid));
  if (byId) return byId;
  const rn = norm(row.name);
  if (!rn) return null;
  return players.find((p) => norm(p.name) === rn) || null;
};

export const PlayerPredictionTab = ({
  title,
  leaderboard,
  players,
  catalogPlayers,
  query,
  setQuery,
  selectedId,
  setSelectedId,
  loading,
  onSubmit,
  fillPlayerQueryFromPick
}) => {
  const playerCatalog = catalogPlayers?.length ? catalogPlayers : players;

  const top3 = leaderboard.slice(0, 3).map((row) => {
    const pl = resolvePlayerFromLeaderboardRow(row, playerCatalog);
    return { ...row, photo: row.photo ?? row.flag ?? pl?.photo };
  });

  const handlePickLeaderboardRow = (row) => {
    if (!row?.id) return;
    const player = resolvePlayerFromLeaderboardRow(row, playerCatalog);
    const idToUse = player ? player.id : row.id;
    setSelectedId(idToUse);
    if (!player?.name && row.name && typeof fillPlayerQueryFromPick === 'function') {
      fillPlayerQueryFromPick(row.name);
    }
  };

  return (
    <div className="w-full mt-3 grid grid-cols-1 lg:grid-cols-5 gap-4">
      <LeaderboardPanel
        top3={top3}
        rows={leaderboard}
        resolvePhoto={(row) => {
          const pl = resolvePlayerFromLeaderboardRow(row, playerCatalog);
          return row.photo ?? row.flag ?? pl?.photo;
        }}
        onPickRow={handlePickLeaderboardRow}
      />
      <VotePanel
        title={title}
        query={query}
        onQueryChange={setQuery}
        onSubmit={onSubmit}
        loading={loading}
        items={players}
        selectedId={selectedId}
        onSelect={(player) => setSelectedId(player.id)}
        emptyText="Không tìm thấy cầu thủ phù hợp."
        showTeamName
        getItemPhoto={(player) => player.photo}
        catalogItems={playerCatalog}
        catalogKind="player"
        suggestionGroupLabel="Danh sách cầu thủ"
      />
    </div>
  );
};

